import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { JobApiResponse, TodoCreate, TodoResponse, TodoUpdate, useApiClient } from "@/lib/api";
import { X, Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import { TagSelector } from "./TagSelector";
import { cn } from "@/lib/utils";

const NO_JOB_VALUE = "__none__";
const NO_REMINDER_VALUE = "__none__";

// Reminder options for the dropdown
const REMINDER_OPTIONS = [
  { value: NO_REMINDER_VALUE, label: 'No reminder' },
  { value: 'day-of', label: 'Day of' },
  { value: 'day-before', label: '1 day before' },
  { value: '2-days-before', label: '2 days before' },
  { value: '3-days-before', label: '3 days before' },
  { value: 'week-before', label: '1 week before' },
  { value: '2-weeks-before', label: '2 weeks before' }
];

// Helper function to calculate reminder date based on due date and option
const calculateReminderDate = (dueDate: string, reminderOption: string): string | undefined => {
  if (!dueDate || !reminderOption || reminderOption === NO_REMINDER_VALUE) return undefined;
  
  const due = new Date(dueDate);
  const reminder = new Date(due);
  
  switch (reminderOption) {
    case 'day-of':
      // Same day as due date
      break;
    case 'day-before':
      reminder.setDate(reminder.getDate() - 1);
      break;
    case '2-days-before':
      reminder.setDate(reminder.getDate() - 2);
      break;
    case '3-days-before':
      reminder.setDate(reminder.getDate() - 3);
      break;
    case 'week-before':
      reminder.setDate(reminder.getDate() - 7);
      break;
    case '2-weeks-before':
      reminder.setDate(reminder.getDate() - 14);
      break;
    default:
      return undefined;
  }
  
  return reminder.toISOString();
};

// Helper function to determine reminder option from dates
const getReminderOption = (dueDate?: string, remindAt?: string): string => {
  if (!dueDate || !remindAt) return NO_REMINDER_VALUE;
  
  const due = new Date(dueDate);
  const remind = new Date(remindAt);
  const diffDays = Math.round((due.getTime() - remind.getTime()) / (1000 * 60 * 60 * 24));
  
  switch (diffDays) {
    case 0: return 'day-of';
    case 1: return 'day-before';
    case 2: return '2-days-before';
    case 3: return '3-days-before';
    case 7: return 'week-before';
    case 14: return '2-weeks-before';
    default: return NO_REMINDER_VALUE;
  }
};

interface TodoFormProps {
  todo?: TodoResponse;
  jobs: JobApiResponse[];
  onSubmit: (data: TodoCreate | TodoUpdate) => void;
  onCancel: () => void;
  isLoading?: boolean;
  openCalendar?: boolean; // New prop to auto-open calendar
  embedded?: boolean; // When true, render without Card chrome (for dialogs)
  lockedJobId?: string; // When provided, force associate to this job and hide/ignore job selection
}

export const TodoForm: React.FC<TodoFormProps> = ({
  todo,
  jobs = [],
  onSubmit,
  onCancel,
  isLoading = false,
  openCalendar = false,
  embedded = false,
  lockedJobId
}) => {
  const { api } = useApiClient();
  const [userTags, setUserTags] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: todo?.title || '',
    description: todo?.description || '',
    job_id: lockedJobId ?? (todo?.job_id || NO_JOB_VALUE),
    priority: todo?.priority || 'medium' as 'low' | 'medium' | 'high',
    due_date: todo?.due_date ? new Date(todo.due_date).toISOString().slice(0, 10) : '', // Date only
    reminder_option: getReminderOption(todo?.due_date, todo?.remind_at),
    tags: todo?.tags || []
  });

  useEffect(() => {
    if (todo) {
      setFormData({
        title: todo.title,
        description: todo.description,
        job_id: lockedJobId ?? (todo.job_id || NO_JOB_VALUE),
        priority: todo.priority,
        due_date: todo.due_date ? new Date(todo.due_date).toISOString().slice(0, 10) : '',
        reminder_option: getReminderOption(todo.due_date, todo.remind_at),
        tags: todo.tags || []
      });
    }
  }, [todo, lockedJobId]);

  useEffect(() => {
    api.getUserTags().then(setUserTags).catch(() => setUserTags([]));
  }, [api]);

  // Auto-focus calendar if openCalendar prop is true
  useEffect(() => {
    if (openCalendar) {
      const dateInput = document.getElementById('due_date');
      if (dateInput) {
        setTimeout(() => {
          dateInput.focus();
          (dateInput as HTMLInputElement).showPicker?.();
        }, 100);
      }
    }
  }, [openCalendar]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const remindAt = calculateReminderDate(formData.due_date, formData.reminder_option);
    
    const submitData: TodoCreate | TodoUpdate = {
      title: formData.title,
      description: formData.description || undefined,
      job_id: lockedJobId ?? (formData.job_id === NO_JOB_VALUE ? undefined : formData.job_id),
      priority: formData.priority,
      due_date: formData.due_date ? new Date(formData.due_date + 'T00:00:00').toISOString() : undefined,
      remind_at: remindAt,
      tags: formData.tags
    };
    onSubmit(submitData);
  };

  const handleChange = (field: string, value: string | string[]) => {
    // Ignore job_id changes when locked
    if (field === 'job_id' && lockedJobId) {
      return;
    }
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Clear reminder option when due date is cleared
      if (field === 'due_date' && !value) {
        newData.reminder_option = NO_REMINDER_VALUE;
      }
      
      return newData;
    });
  };

  return (
    <Card className={cn("w-full max-w-2xl mx-auto", embedded && "shadow-none border-0 p-0") }>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-lg font-semibold">
          {todo ? 'Edit Todo' : 'Create New Todo'}
        </CardTitle>
        {!embedded && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter todo title..."
              required
            />
          </div>
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Add more details about this task..."
              rows={3}
            />
          </div>
          {/* Job Association - hidden when locked */}
          {!lockedJobId && (
            <div className="space-y-2">
              <Label htmlFor="job_id">Associated Job (Optional)</Label>
              <Select
                value={formData.job_id}
                onValueChange={(value) => handleChange('job_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a job (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_JOB_VALUE}>None</SelectItem>
                  {(jobs ?? []).map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.company} - {job.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {/* Due Date */}
          <div className="space-y-2">
            <Label htmlFor="due_date">Due On</Label>
            <div className="flex gap-2">
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
                className="flex-1"
              />
              {formData.due_date && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleChange('due_date', '')}
                  className="flex-shrink-0"
                  title="Clear due date"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {/* Reminder */}
          <div className="space-y-2">
            <Label htmlFor="reminder_option">Remind Me</Label>
            <Select
              value={formData.reminder_option}
              onValueChange={(value) => handleChange('reminder_option', value)}
              disabled={!formData.due_date}
            >
              <SelectTrigger>
                <SelectValue placeholder={formData.due_date ? "Select reminder timing" : "Set a due date first"} />
              </SelectTrigger>
              <SelectContent>
                {REMINDER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!formData.due_date && (
              <p className="text-xs text-gray-500">
                Set a due date to enable reminders
              </p>
            )}
          </div>
          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <TagSelector
              value={formData.tags}
              onChange={tags => handleChange('tags', tags)}
              userTags={userTags}
              disabled={isLoading}
            />
          </div>
          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !formData.title.trim()}
            >
              {isLoading ? 'Saving...' : todo ? 'Update Todo' : 'Create Todo'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
